// Estado do app: um Snapshot em memória. `executar` manda um Comando (dado
// puro), aplica o novo estado, avisa os inscritos e mostra um toast do
// resultado (sucesso ou erro amigável).
//
// Dois jeitos de rodar o comando:
//   - LOCAL (versão de arquivo): roda `executarComando` aqui e `repo.save`.
//   - HOSPEDADO (versão web): manda pro servidor (`transporte`), que roda o
//     mesmo executor e devolve o Snapshot novo; nada de `repo.save` aqui.
//
// Duas garantias que o Store dá aos chamadores:
//   1. O estado em memória só avança **depois** que a gravação concluiu — se
//      falha, `get()` continua no último estado bom, sem divergir em silêncio.
//   2. Os comandos são aplicados **em ordem** (fila única), mesmo se as
//      chamadas se sobrepuserem.

import type { Repository } from "../domain/ports.ts";
import type { Snapshot } from "../domain/snapshot.ts";
import { agoraISO, hojeISO } from "../domain/datas.ts";
import { executarComando, type Comando } from "../comandos/index.ts";
import { criarFilaSequencial } from "../lib/fila-sequencial.ts";

/** Para onde vão as mensagens de resultado. Em produção, os toasts. */
export interface Avisos {
  ok(texto: string): void;
  erro(texto: string): void;
}

/**
 * Modo hospedado: roda o comando no servidor e devolve o Snapshot resultante.
 * Deve rejeitar (com uma `Error` de mensagem amigável) quando o servidor
 * recusa o comando.
 */
export type TransporteComando = (comando: Comando) => Promise<Snapshot>;

export interface Store {
  get(): Snapshot;
  /**
   * Aplica um `Comando`, grava e notifica. Mostra um toast "ok" com `msgOk` ao
   * concluir (padrão "Alterações salvas."; `null` = sem toast).
   *
   * Falhou: o estado **não** muda, ninguém é notificado, e o erro é propagado.
   * A mensagem amigável vai para `aoErro` quando ele é dado — e nesse caso
   * **não** vira toast: a pessoa nunca vê a mesma mensagem duas vezes.
   */
  executar(
    comando: Comando,
    msgOk?: string | null,
    aoErro?: (mensagem: string) => void,
  ): Promise<void>;
  /**
   * A primitiva por trás do `executar`: roda `produzir` na fila única, adota o
   * Snapshot que ele devolve, notifica e tosta — mesmas garantias. Para o que
   * não é comando puro (ex.: criar usuário, que precisa do bcrypt do servidor).
   */
  aplicar(
    produzir: () => Promise<Snapshot>,
    msgOk?: string | null,
    aoErro?: (mensagem: string) => void,
  ): Promise<void>;
  subscribe(listener: () => void): () => void;
}

function mensagemAmigavel(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  return "Não foi possível concluir a operação. Tente novamente.";
}

export function createStore(
  initial: Snapshot,
  repo: Repository,
  avisos: Avisos,
  /** Dado = modo hospedado; ausente = modo local (arquivo). */
  transporte?: TransporteComando,
): Store {
  let state = initial;
  const listeners = new Set<() => void>();
  const enfileirar = criarFilaSequencial();

  const aplicar = async (
    proximo: () => Promise<Snapshot>,
    msgOk: string | null,
    aoErro?: (mensagem: string) => void,
  ): Promise<void> => {
    let next: Snapshot;
    try {
      next = await proximo();
    } catch (e) {
      const msg = mensagemAmigavel(e);
      // ou o chamador mostra, ou o toast mostra — nunca os dois
      if (aoErro) aoErro(msg);
      else avisos.erro(msg);
      throw e; // estado intacto: a gravação não confirmou
    }
    state = next;
    for (const l of listeners) l();
    if (msgOk !== null) avisos.ok(msgOk);
  };

  const rodar = transporte
    ? (comando: Comando) => transporte(comando)
    : async (comando: Comando) => {
        const next = executarComando(state, comando, { hoje: hojeISO(), agora: agoraISO() });
        await repo.save(next);
        return next;
      };

  return {
    get: () => state,
    executar: (comando, msgOk = "Alterações salvas.", aoErro) =>
      enfileirar(() => aplicar(() => rodar(comando), msgOk, aoErro)),
    aplicar: (produzir, msgOk = "Alterações salvas.", aoErro) =>
      enfileirar(() => aplicar(produzir, msgOk, aoErro)),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
